import React from 'react';
import { Alert, Button, Form, Input, Modal, Radio, Space, Table, Tag, Typography, message } from 'antd';
import { KeyOutlined, PlusCircleOutlined, StopOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useQuery } from 'react-query';
import MainContent from '@/layouts/MainContent';
import StationInput from '@/components/FormItems/StationInput';
import BusInput from '@/components/FormItems/BusInput';
import moment from 'moment';
import { DEFAULT_DISPLAY_DATE_TIME_FORMAT, ModalWidth, VALIDATE_REQUIRED } from '@/config';
import { defaultApiErrorHandle } from '@/helpers/errorHandler';
import { TerminalEnrollmentCodeResponse, TerminalModel } from '@/__generated';
import { apiSdk } from '@/httpClient';

const PAGE_TITLE = 'terminalsPage_Title';
const StationScope = 1;
const VehicleScope = 2;
const terminalsKey = 'company_terminals';

// Date et heure locales sur 24 h (le format partagé ajoute « AM/PM » à une heure déjà sur 24 h).
const formatDateTime = (v?: string | Date) => (v ? moment(v).format(DEFAULT_DISPLAY_DATE_TIME_FORMAT) : '');

const statusColor: Record<string, string> = { Active: 'green', PendingEnrollment: 'gold', Revoked: 'red', Retired: 'default' };

/**
 * Terminaux de la compagnie (P35) : un terminal physique (T2, T2s, V3 MIX…) rattaché à une gare ou à un véhicule,
 * enregistré une fois sur l'appareil avec un code à usage unique, puis utilisé par plusieurs agents.
 */
export default function TerminalsPage() {
  const { t } = useTranslation();
  const { data, isLoading, refetch } = useQuery(terminalsKey, () => apiSdk.TerminalService.terminals());
  const [createVisible, setCreateVisible] = React.useState(false);
  const [code, setCode] = React.useState<TerminalEnrollmentCodeResponse>();
  const [revoking, setRevoking] = React.useState<TerminalModel>();
  const [saving, setSaving] = React.useState(false);
  const [form] = Form.useForm();
  const [revokeForm] = Form.useForm();
  const [retireForm] = Form.useForm();
  const [retiring, setRetiring] = React.useState<TerminalModel>();
  const scopeType = Form.useWatch('scopeType', form);

  const showCode = (response: TerminalEnrollmentCodeResponse) => {
    if (response.success && response.enrollmentCode) {
      setCode(response);
      refetch();
    } else {
      message.error(t(response.message || 'terminalsPage_Error'));
    }
  };

  const create = (values: any) => {
    setSaving(true);
    apiSdk.TerminalService.terminals1({ body: { name: values.name, scopeType: values.scopeType, scopeId: values.scopeId } })
      .then(response => {
        if (response.success) {
          setCreateVisible(false);
          form.resetFields();
        }
        showCode(response);
      })
      .catch(defaultApiErrorHandle)
      .finally(() => setSaving(false));
  };

  const renewCode = (terminal: TerminalModel) => {
    Modal.confirm({
      title: t('terminalsPage_NewCodeConfirmTitle', { name: terminal.name }),
      content: t('terminalsPage_NewCodeConfirmContent'),
      okText: t('terminalsPage_NewCode'),
      cancelText: t('cancel'),
      onOk: () => apiSdk.TerminalService.enrollmentCode({ id: terminal.id || '' }).then(showCode).catch(defaultApiErrorHandle),
    });
  };

  const revoke = (values: any) => {
    if (!revoking) return;
    setSaving(true);
    apiSdk.TerminalService.revoke({ id: revoking.id || '', body: { reason: values.reason } })
      .then(response => {
        if (response.success) {
          message.success(t('terminalsPage_Revoked'));
          setRevoking(undefined);
          revokeForm.resetFields();
          refetch();
        } else {
          message.error(t(response.message || 'terminalsPage_Error'));
        }
      })
      .catch(defaultApiErrorHandle)
      .finally(() => setSaving(false));
  };

  const retire = (values: any) => {
    if (!retiring) return;
    setSaving(true);
    apiSdk.TerminalService.retire({ id: retiring.id || '', body: { reason: values.reason } })
      .then(response => {
        if (response.success) {
          message.success(t('terminalsPage_Retired'));
          setRetiring(undefined);
          retireForm.resetFields();
          refetch();
        } else {
          message.error(t(response.message || 'terminalsPage_Error'));
        }
      })
      .catch(defaultApiErrorHandle)
      .finally(() => setSaving(false));
  };

  const columns = [
    { title: t('terminalsPage_Name'), dataIndex: 'name' },
    {
      title: t('terminalsPage_Scope'),
      dataIndex: 'scopeName',
      render: (v: string, r: TerminalModel) => `${r.scopeType === VehicleScope ? t('terminalsPage_Vehicle') : t('terminalsPage_Station')} · ${v || ''}`,
    },
    {
      title: t('terminalsPage_Status'),
      dataIndex: 'status',
      render: (v: string) => <Tag color={statusColor[v] || 'default'}>{t(`terminalsPage_Status_${v}`)}</Tag>,
    },
    {
      title: t('terminalsPage_StatusDetail'),
      key: 'statusDetail',
      render: (_: any, r: TerminalModel) =>
        r.status === 'Revoked'
          ? [formatDateTime(r.revokedOn), r.revokedByName, r.revocationReason].filter(Boolean).join(' · ')
          : r.status === 'Retired'
          ? [formatDateTime(r.retiredOn), r.retiredByName, r.retirementReason].filter(Boolean).join(' · ')
          : null,
    },
    { title: t('terminalsPage_DeviceModel'), dataIndex: 'deviceModel' },
    { title: t('terminalsPage_LastSeen'), dataIndex: 'lastSeenOn', render: (v: string) => formatDateTime(v) },
    { title: t('terminalsPage_LastAgent'), dataIndex: 'lastUserName' },
    {
      title: t('terminalsPage_Actions'),
      key: 'actions',
      render: (_: any, r: TerminalModel) =>
        r.status === 'Revoked' ? null : (
          <Space>
            {r.status !== 'Retired' ? (
              <>
                <Button size="small" icon={<KeyOutlined />} onClick={() => renewCode(r)}>
                  {t('terminalsPage_NewCode')}
                </Button>
                <Button size="small" onClick={() => setRetiring(r)}>
                  {t('terminalsPage_Retire')}
                </Button>
              </>
            ) : null}
            <Button size="small" danger icon={<StopOutlined />} onClick={() => setRevoking(r)}>
              {t('terminalsPage_Revoke')}
            </Button>
          </Space>
        ),
    },
  ];

  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={[
        <Button key="create" type="primary" className="nta-create-btn" icon={<PlusCircleOutlined />} onClick={() => setCreateVisible(true)}>
          {t('terminalsPage_Create')}
        </Button>,
      ]}
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <Typography.Paragraph type="secondary">{t('terminalsPage_Intro')}</Typography.Paragraph>
        <Table rowKey="id" loading={isLoading} dataSource={data || []} columns={columns as any} pagination={false} bordered size="middle" />
      </div>

      <Modal
        visible={createVisible}
        title={t('terminalsPage_Create')}
        width={ModalWidth.small}
        okText={t('terminalsPage_CreateOk')}
        cancelText={t('cancel')}
        confirmLoading={saving}
        onCancel={() => setCreateVisible(false)}
        onOk={() => form.validateFields().then(create).catch(() => {})}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ scopeType: StationScope }} preserve={false}>
          <Form.Item label={t('terminalsPage_Name')} name="name" rules={[{ required: true, whitespace: true, message: VALIDATE_REQUIRED }]}>
            <Input placeholder={t('terminalsPage_NamePlaceholder')} maxLength={100} />
          </Form.Item>
          <Form.Item label={t('terminalsPage_Scope')} name="scopeType">
            <Radio.Group onChange={() => form.setFieldValue('scopeId', undefined)}>
              <Radio value={StationScope}>{t('terminalsPage_Station')}</Radio>
              <Radio value={VehicleScope}>{t('terminalsPage_Vehicle')}</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={scopeType === VehicleScope ? t('terminalsPage_Vehicle') : t('terminalsPage_Station')}
            name="scopeId"
            rules={[{ required: true, message: VALIDATE_REQUIRED }]}
          >
            {scopeType === VehicleScope ? <BusInput /> : <StationInput />}
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        visible={!!code}
        title={t('terminalsPage_CodeTitle', { name: code?.terminal?.name })}
        width={ModalWidth.small}
        footer={[
          <Button key="ok" type="primary" onClick={() => setCode(undefined)}>
            {t('terminalsPage_CodeDone')}
          </Button>,
        ]}
        closable={false}
        maskClosable={false}
      >
        <Alert type="warning" showIcon message={t('terminalsPage_CodeOnce')} style={{ marginBottom: 16 }} />
        <div style={{ textAlign: 'center' }}>
          <Typography.Text copyable={{ text: code?.enrollmentCode || '' }} strong style={{ fontSize: 32, fontFamily: 'monospace', letterSpacing: 4 }}>
            {code?.enrollmentCode}
          </Typography.Text>
        </div>
        <Typography.Paragraph style={{ marginTop: 16 }}>
          {t('terminalsPage_CodeValidity', { date: formatDateTime(code?.enrollmentCodeExpiresOn) })}
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary">{t('terminalsPage_CodeHowTo')}</Typography.Paragraph>
      </Modal>

      <Modal
        visible={!!retiring}
        title={t('terminalsPage_RetireTitle', { name: retiring?.name })}
        width={ModalWidth.small}
        okText={t('terminalsPage_Retire')}
        cancelText={t('cancel')}
        confirmLoading={saving}
        onCancel={() => setRetiring(undefined)}
        onOk={() => retireForm.validateFields().then(retire).catch(() => {})}
        destroyOnClose
      >
        <Alert type="warning" showIcon message={t('terminalsPage_RetireWarning')} style={{ marginBottom: 16 }} />
        <Form form={retireForm} layout="vertical" preserve={false}>
          <Form.Item label={t('terminalsPage_RetireReason')} name="reason">
            <Input.TextArea rows={3} maxLength={200} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        visible={!!revoking}
        title={t('terminalsPage_RevokeTitle', { name: revoking?.name })}
        width={ModalWidth.small}
        okText={t('terminalsPage_Revoke')}
        okButtonProps={{ danger: true }}
        cancelText={t('cancel')}
        confirmLoading={saving}
        onCancel={() => setRevoking(undefined)}
        onOk={() => revokeForm.validateFields().then(revoke).catch(() => {})}
        destroyOnClose
      >
        <Alert type="error" showIcon message={t('terminalsPage_RevokeWarning')} style={{ marginBottom: 16 }} />
        <Form form={revokeForm} layout="vertical" preserve={false}>
          <Form.Item label={t('terminalsPage_RevokeReason')} name="reason" rules={[{ required: true, whitespace: true, message: VALIDATE_REQUIRED }]}>
            <Input.TextArea rows={3} maxLength={200} />
          </Form.Item>
        </Form>
      </Modal>
    </MainContent>
  );
}
